import { Link } from 'react-router-dom';
import type { ApplicationStatus, DashApplication } from '../api/client';
import { useDashboard } from '../api/insights';
import StatusBadge from './StatusBadge';

const PIPELINE_ORDER: ApplicationStatus[] = [
  'PROPOSED', 'UNDER_REVIEW', 'APPROVED', 'INTERVIEWING', 'INTERVIEWED', 'HIRED', 'REJECTED',
];
const PIPELINE_LABEL: Record<ApplicationStatus, string> = {
  PROPOSED: 'Proposed', UNDER_REVIEW: 'Under review', APPROVED: 'Approved',
  INTERVIEWING: 'Interviewing', INTERVIEWED: 'Interviewed', HIRED: 'Hired', REJECTED: 'Rejected',
};

function MiniList({ title, items, empty }: { title: string; items: DashApplication[]; empty: string }) {
  return (
    <div className="panel" style={{ marginTop: '1rem' }}>
      <h3>{title}</h3>
      {items.length === 0 ? (
        <p className="muted">{empty}</p>
      ) : (
        <ul className="mini-list">
          {items.map((a) => (
            <li key={a.id}>
              <Link to={`/applications/${a.id}`}>{a.candidate.fullName}</Link>
              <span className="muted small"> · {a.job.title}</span>
              <span style={{ marginLeft: 'auto' }}><StatusBadge status={a.status} /></span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function Dashboard() {
  const { data, isLoading, error } = useDashboard();
  if (isLoading) return <p className="muted">Loading your dashboard…</p>;
  if (error) return <p className="error">Could not load dashboard: {String(error)}</p>;
  if (!data) return null;

  const isHrAdmin = data.roles.isHr || data.roles.isAdmin;

  return (
    <div style={{ width: '100%', maxWidth: 860 }}>
      {isHrAdmin && (
        <div className="panel">
          <h2>Pipeline</h2>
          <div className="stat-grid">
            {PIPELINE_ORDER.map((s) => (
              <div key={s} className="stat">
                <span className="stat-num">{data.pipeline?.[s] ?? 0}</span>
                <span className="stat-label">{PIPELINE_LABEL[s]}</span>
              </div>
            ))}
          </div>
          {data.jobs && (
            <p className="muted small" style={{ marginTop: '0.75rem' }}>
              Jobs — open {data.jobs.OPEN ?? 0} · draft {data.jobs.DRAFT ?? 0} · closed {data.jobs.CLOSED ?? 0}
            </p>
          )}
        </div>
      )}
      {isHrAdmin && <MiniList title="Awaiting final decision" items={data.awaitingDecision ?? []} empty="No candidates waiting on a hire decision." />}
      {isHrAdmin && <MiniList title="Under review" items={data.underReview ?? []} empty="No candidates currently under review." />}
      {data.roles.isReviewer && <MiniList title="Awaiting your review" items={data.toReview ?? []} empty="You're all caught up on reviews." />}
      {data.roles.isInterviewer && <MiniList title="Awaiting your feedback" items={data.toInterview ?? []} empty="No interviews waiting on your feedback." />}
      {!isHrAdmin && !data.roles.isReviewer && !data.roles.isInterviewer && (
        <div className="panel"><p className="muted">You don't have any roles yet. An administrator needs to grant you access.</p></div>
      )}
    </div>
  );
}
